import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getApp } from 'services/appService';
import { useAppSelector } from 'hooks/reduxHooks';
import { RootState } from 'redux-setup/store';
import { toast } from 'react-toastify';
import { getStage } from 'services/stageService';
import { IStage } from 'interface/stageInterface';
import { IApp } from 'interface/appInterface';
import { getDeploymentVersion } from 'services/deploymentService';
import { IDeploymentVersion } from 'interface/deploymentInterface';
import { listRepositoriesReliant } from 'services/repositoryService';
import { IRepository } from 'interface/repositoryInterface';
import { Loader } from 'components/common/loader';
import './styles.scss';

export const ServicesDetails = () => {
  const { id, repositoryID, deploymentVersionID } = useParams();
  const stages = useAppSelector((state: RootState) => state.stages.stages);
  const appStageIDs = stages.filter(
    stage => stage.RepositoryID === repositoryID,
  );
  const organizationID = useAppSelector(
    (state: RootState) => state.organization.id,
  );

  const [appResults, setAppResults] = useState<IApp>();
  const [stageResults, setStageResults] = useState<IStage[]>([]);
  const [deploymentVersionResults, setDeploymentVersionResults] =
    useState<IDeploymentVersion>();
  const [repositoryResults, setRepositoryResults] = useState<IRepository>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await handleGetApp();
      await handleGetDeploymentVersion();
      for (const stage of appStageIDs) {
        await handleGetStage(stage.ID);
      }
      await handleGetRepository();
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleGetApp = async () => {
    if (!id || !repositoryID) {
      toast.error('Error fetching app details');
      return;
    }
    const response = await getApp({
      AppID: id,
      OrganizationID: organizationID,
      RepositoryID: repositoryID,
    });
    const err = response.check();
    if (err) {
      toast.error('Error fetching app');
      return;
    }

    const app = response.unwrap();
    if (app.ID) {
      setAppResults(app);
    }
  };

  const handleGetStage = async (stageId: string) => {
    if (!repositoryID) {
      toast.error('Error fetching stage');
      return;
    }
    const response = await getStage({
      SliceID: stageId,
      OrganizationID: organizationID,
      RepositoryID: repositoryID,
    });
    const err = response.check();
    if (err) {
      toast.error('Error fetching stage');
      return;
    }
    const stage = response.unwrap();
    if (stage.ID) {
      setStageResults(prev => [...prev, stage]);
    }
  };

  const handleGetDeploymentVersion = async () => {
    if (!deploymentVersionID) {
      toast.error('Error fetching deployment version');
      return;
    }
    const response = await getDeploymentVersion({
      DeploymentVersionID: deploymentVersionID,
      OrganizationID: organizationID,
    });
    const err = response.check();
    if (err) {
      toast.error('Error fetching deployment version');
      return;
    }
    const deploymentVersion = response.unwrap();
    if (deploymentVersion.ID) {
      setDeploymentVersionResults(deploymentVersion);
    }
  };

  const handleGetRepository = async () => {
    if (!repositoryID) {
      toast.error('Error fetching repository');
      return;
    }
    const response = await listRepositoriesReliant({
      OrganizationID: organizationID,
    });
    const err = response.check();
    if (err) {
      toast.error('Error fetching repository');
      return;
    }
    const repository = response.unwrap();
    if (repository.Result) {
      const repositoryData = repository.Result.find(
        (repo: IRepository) => repo.ID === repositoryID,
      );
      if (repositoryData) {
        setRepositoryResults(repositoryData);
      }
    }
  };

  return (
    <>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <div className="service-details-container">
          <div className="service-details-content">
            <div className="service-details">
              <div className="service-details-item">
                <h2 className="service-title">App Details</h2>
                <div className="app-details-grid">
                  {appResults && (
                    <div className="app-info-card">
                      <div className="app-info-row">
                        <span className="app-info-label">Name:</span>
                        <span className="app-info-value">
                          {appResults.Name}
                        </span>
                      </div>
                      <div className="app-info-row">
                        <span className="app-info-label">Status:</span>
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                            appResults.Status?.toLowerCase() === 'active'
                              ? 'bg-[#E7F6EC] text-[#027A48]'
                              : appResults.Status?.toLowerCase() === 'failed'
                                ? 'bg-[#FEE4E2] text-[#D92D20]'
                                : appResults.Status?.toLowerCase() ===
                                    'deploying'
                                  ? 'bg-[#EFF8FF] text-[#2E90FA]'
                                  : 'bg-gray-100 text-gray-700'
                          }`}>
                          {appResults.Status || 'Unknown'}
                        </span>
                      </div>
                      <div className="app-info-row">
                        <span className="app-info-label">Version:</span>
                        <span className="app-info-value">
                          {deploymentVersionResults?.AppVersion}
                        </span>
                      </div>
                      <div className="app-info-row">
                        <span className="app-info-label">Created:</span>
                        <span className="app-info-value">
                          {appResults.CreatedAt
                            ? new Date(appResults.CreatedAt).toLocaleString()
                            : 'No date available'}
                        </span>
                      </div>
                      <div className="app-info-row">
                        <span className="app-info-label">Last Updated:</span>
                        <span className="app-info-value">
                          {appResults.UpdatedAt
                            ? new Date(appResults.UpdatedAt).toLocaleString()
                            : 'No date available'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="service-details-item">
                <h2 className="service-title">Stage Summary</h2>

                <div className="version-grid">
                  {stageResults.length > 0 ? (
                    stageResults.map(
                      stage =>
                        stage && (
                          <div className="version-card" key={stage.ID}>
                            <div className="version-header">
                              <span className="version-icon">↗</span>
                              <h3>{stage.Name}</h3>
                            </div>
                            <div className="version-info borderless">
                              <div className="version-number">
                                {deploymentVersionResults?.AppVersion}
                              </div>
                              <div className="version-date">
                                {stage.UpdatedAt}
                              </div>
                              <div
                                className={`inline-flex items-center rounded-md px-4 py-1 text-sm min-w-[100%] font-medium justify-center ${
                                  stage?.Status?.toLowerCase() === 'active'
                                    ? 'bg-[#E7F6EC] text-[#027A48]'
                                    : stage?.Status?.toLowerCase() === 'failed'
                                      ? 'bg-[#FEE4E2] text-[#D92D20]'
                                      : stage?.Status?.toLowerCase() ===
                                          'deploying'
                                        ? 'bg-[#EFF8FF] text-[#2E90FA]'
                                        : 'bg-gray-100 text-gray-700'
                                }`}>
                                {stage?.Status || 'Unknown'}
                              </div>
                            </div>
                          </div>
                        ),
                    )
                  ) : (
                    <div className="version-card">
                      <h3>No stages found</h3>
                    </div>
                  )}
                </div>
              </div>

              <div className="service-details-item">
                <h2 className="service-title">Repository Information</h2>
                <div className="repository-info-grid">
                  <div className="repository-info-card">
                    <div className="repository-links">
                      <a
                        href={`https://github.com/${repositoryResults?.OwnerName}/${repositoryResults?.RepoName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="repository-link">
                        <span className="icon">↗</span> Repository
                      </a>
                      <a
                        href={`https://github.com/${repositoryResults?.OwnerName}/${repositoryResults?.RepoName}/releases`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="repository-link">
                        <span className="icon">↗</span> Releases
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
